import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { commsApi } from '@/utils/commsApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Handles the return from Emergent Google auth:
//  /dashboard#session_id=...             -> sign in (or create) a Vibely account
//  /dashboard/link-google#session_id=... -> connect Google to the already signed-in account
export default function AuthCallback({ setUser }) {
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.replace(/^#/, '')).get('session_id');
    const linking = location.pathname.startsWith('/dashboard/link-google') && Boolean(localStorage.getItem('session_token'));

    const restoreUser = async () => {
      try { setUser(await commsApi.me()); } catch { /* token invalid: protected routes redirect to login */ }
    };

    const link = async () => {
      try {
        const { user, google_email } = await commsApi.linkGoogle(sessionId);
        setUser(user);
        toast.success(`Google account connected: ${google_email}`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Could not connect Google account');
        await restoreUser();
      }
      navigate('/dashboard/kanban', { replace: true });
    };

    const signIn = async () => {
      try {
        const response = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        localStorage.setItem('session_token', response.data.session_token);
        setUser(response.data.user);
        toast.success(`Signed in as ${response.data.user.name}`);
        navigate('/dashboard/kanban', { replace: true });
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Google sign-in failed');
        navigate('/login', { replace: true });
      }
    };

    if (!sessionId) navigate('/login', { replace: true });
    else if (linking) link();
    else signIn();
  }, [location.hash, location.pathname, navigate, setUser]);

  return (
    <div className="loading-screen" data-testid="auth-callback">
      <div className="spinner"></div>
    </div>
  );
}
